// WHAT: Component for managing SwitchBot access token
// WHY:  Separates token management UI from main component to reduce complexity
// DOMAIN: Settings → SwitchBot
// DEPENDENCIES: React, @/components/ui, lucide-react

import { Edit, Key, Trash2 } from "lucide-react";
import React from "react";

import {
  Badge,
  Button,
  Card,
  Heading,
  Input,
  Label,
  Stack,
  StatusIndicator,
} from "@/components/ui";

interface TokenSectionProperties {
  /** Whether token is being edited */
  editingToken: boolean;
  /** Whether form is in loading state */
  loading: boolean;
  /** Handler for cancel edit */
  onCancelEdit: () => void;
  /** Handler for edit action */
  onEdit: () => void;
  /** Handler for remove action */
  onRemove: () => void;
  /** Handler for save action */
  onSave: () => void;
  /** Handler for token change */
  onTokenChange: (value: string) => void;
  /** Saved token value */
  savedToken?: null | string;
  /** Token input value */
  token: string;
}

/**
 * WHAT:
 *   Component for managing SwitchBot access token.
 *
 * WHY:
 *   Separates token management UI from main component to reduce complexity.
 *
 * @param root0
 *
 * @param root0.editingToken
 *
 * @param root0.loading
 *
 * @param root0.savedToken
 *
 * @param root0.token
 *
 * @param root0.onEdit
 *
 * @param root0.onRemove
 *
 * @param root0.onSave
 *
 * @param root0.onTokenChange
 *
 * @param root0.onCancelEdit
 *
 * @returns {JSX.Element} Token section component
 *
 */
export const TokenSection: React.FC<TokenSectionProperties> = ({
  editingToken,
  loading,
  onCancelEdit,
  onEdit,
  onRemove,
  onSave,
  onTokenChange,
  savedToken,
  token,
}) => {
  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <Stack direction="col" gap={4}>
        <Stack align="center" direction="row" gap={3}>
          <Key className="w-5 h-5 text-blue-400" />
          <Heading className="text-white" level="h3" size="md">
            Access Token
          </Heading>
          <Badge size="sm" variant={savedToken ? "success" : "warning"}>
            {savedToken ? "Configured" : "Not Set"}
          </Badge>
        </Stack>

        {savedToken && !editingToken ? (
          <Stack direction="col" gap={3}>
            <StatusIndicator
              configuredText="Configured"
              description="Your SwitchBot access token is configured and ready to use"
              isConfigured
              label="SwitchBot Access Token"
              size="md"
            />
            <Stack direction="row" gap={2}>
              <Button
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                onClick={onEdit}
                size="sm"
                variant="outline"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                onClick={() => {
                  onRemove();
                }}
                size="sm"
                variant="outline"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="col" gap={3}>
            <StatusIndicator
              description="Enter your SwitchBot access token to enable smart home automation"
              isConfigured={false}
              label="SwitchBot Access Token"
              notConfiguredText="Not Set"
              size="md"
            />
            <Stack direction="col" gap={2}>
              <Label className="text-slate-300" htmlFor="token-input">
                Access Token
              </Label>
              <Input
                className="bg-slate-700/50 border-slate-600/50"
                id="token-input"
                onChange={(e) => {
                  onTokenChange(e.target.value);
                }}
                placeholder="Enter your SwitchBot access token..."
                type="password"
                value={token}
              />
              <Stack direction="row" gap={2}>
                <Button
                  className="bg-blue-600/80 hover:bg-blue-600 border border-blue-500/50 hover:border-blue-500 text-white"
                  disabled={loading || !token}
                  onClick={() => {
                    onSave();
                  }}
                >
                  {loading ? "Saving..." : "Save Token"}
                </Button>
                {editingToken ? (
                  <Button onClick={onCancelEdit} size="sm" variant="outline">
                    Cancel
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

